import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import i18next from 'i18next';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextBoxController } from '@components-clean/core/text-box';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { DataViewConfig, PanelData } from '@pages-clean/admin/study/admin-data-views/data-views/types';
import { datasourceLOV } from '@pages-clean/admin/study/admin-data-views/utils';
import {
    getPanels_OpraQueries_panels_Panel,
    addDataViewConfig,
    addDataViewConfig_OpraMutations_addDataViewConfig_DataViewConfigurationResult,
} from 'queries/generated/central-monitoring.generated';
import Constants from '@utils/constants';
import { SAVE_DATA_VIEW_CONFIGURATION } from '@queries/central-monitoring';

type AddForm = {
    active: boolean;
    name: string;
    mainPanel: getPanels_OpraQueries_panels_Panel | null;
    datasource: (typeof Constants.DataSourceLOV)[0] | null;
};

type AddDataViewDialogProps = {
    studyRef: string;
    onClose: () => void;
    existingViews: DataViewConfig[];
    panels: PanelData[];
    handleSubmitResponse: (
        data?: addDataViewConfig_OpraMutations_addDataViewConfig_DataViewConfigurationResult | null,
    ) => void;
};

const COMPONENT_NAME = 'add-data-view-dialog';

export const AddDataViewDialog = ({
    studyRef,
    onClose,
    existingViews,
    panels,
    handleSubmitResponse,
}: AddDataViewDialogProps) => {
    const { t } = useTranslation();
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const [saveDataView, { loading: isSaving }] = useMutation<addDataViewConfig>(SAVE_DATA_VIEW_CONFIGURATION);
    const submit = (data: AddForm) => {
        saveDataView({
            variables: {
                studyId: studyRef,
                isActive: data.active,
                name: data.name.trim(),
                mainPanelId: data.mainPanel!.id as string,
                dataSource: data.datasource!.code,
            },
        })
            .then((resp) => {
                handleSubmitResponse(resp.data?.addDataViewConfig);
            })
            .catch((err) => {
                window.console.error(err);
            });
    };

    const {
        control,
        handleSubmit,
        // Need to subscribe to the errors state, so that the nameFieldState.invalid gets updated correctly
        // even before the form is submitted. As for why errors and not isValid, no idea.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        formState: { isDirty, errors, isValid },
        getFieldState,
        trigger,
    } = useForm<AddForm>({
        defaultValues: {
            active: true,
            name: '',
            mainPanel: null,
            datasource: null,
        },
        mode: 'all',
    });

    const nameFieldState = getFieldState('name');

    useEffect(() => {
        if (isDirty) trigger().catch((err) => window.console.error(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [existingViews]);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isDirty]);

    const onCloseHandler = async () => {
        if (componentHasChanges(COMPONENT_NAME)) {
            const confirmed = await confirmDiscardChanges('add-data-view-confirmation');
            if (!confirmed) {
                return;
            }
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        onClose();
    };

    return (
        <DraggableDialog
            dataTestId="add-data-view-dialog"
            title={t('admin:dataView.addDataView')}
            primaryBtnLabel={t('add')}
            secondaryBtnLabel={t('cancel')}
            primaryBtnDisabled={nameFieldState.invalid || !isDirty || isSaving}
            onSubmit={() => handleSubmit(submit)()}
            onClose={onCloseHandler}
            height={325}
            width={675}
        >
            <form id="add-data-view-form" className="k-w-full k-d-flex k-flex-column k-gap-3">
                <Controller
                    control={control}
                    name="active"
                    render={({ field: { onChange, value } }) => {
                        return (
                            <CoreCheckbox
                                data-testid="active-data-view"
                                onChange={onChange}
                                value={value}
                                defaultChecked
                                label={t('active')}
                            />
                        );
                    }}
                />
                <div style={{ width: '320px' }}>
                    <TextBoxController
                        control={control}
                        name="name"
                        data-testid="data-view-name"
                        label={`${t('admin:dataViewName')}*`}
                        maxLength={30}
                        required
                        validateRules={{
                            uniqueValue: (value) => {
                                if (
                                    value &&
                                    existingViews.find(
                                        (x) =>
                                            x.name.toLocaleLowerCase().trim() ===
                                            (value as string).toLocaleLowerCase().trim(),
                                    )
                                )
                                    return i18next.t('admin:dataView.validation.uniqueName');
                                return true;
                            },
                            whitespace: (value) => {
                                if (value && !(value as string).trim().length)
                                    return i18next.t('ra:requiredFieldError');
                            },
                        }}
                    />
                </div>
                <BasicDropDownController
                    control={control}
                    name="mainPanel"
                    data-testid="data-view-main-panel"
                    data={panels.filter((p) => p.active && !existingViews.find((v) => v.mainPanelId === p.id))}
                    textField="title"
                    dataItemKey="id"
                    required
                    label={`${t('admin:dataViewMainPanel')}*`}
                    filterable
                    allowClear
                    validateRules={{
                        uniqueValue: (value) => {
                            if (existingViews.find((x) => x.mainPanelId === value?.id))
                                return i18next.t('admin:dataView.validation.uniqueMainPanel');
                            return true;
                        },
                    }}
                />
                <div style={{ width: '320px' }}>
                    <BasicDropDownController
                        control={control}
                        name="datasource"
                        data-testid="data-view-datasource"
                        label={`${t('admin:dataViewDataSource')}*`}
                        data={datasourceLOV}
                        textField="key"
                        dataItemKey="value"
                        required
                        filterable
                        allowClear
                    />
                </div>
            </form>
        </DraggableDialog>
    );
};
